import { IsoTimestamp, Uuid } from '../dto/common.js';
import { z } from '../zod.js';
import { DERIVED_STATE_STATUSES, DERIVER_NAMES, DERIVER_TRIGGERS, DERIVERS, DRIFT_KINDS, } from './rows-derivers.js';
/**
 * The Deriver contract (baseline section 3 "Derived registry" and section 4 "Save pipeline";
 * 1.0b deliverable 5). One pure deriver per artifact; `pnpm db:rebuild <name>` recomputes one,
 * `pnpm db:verify` recomputes into temporary tables and reports drift, and a changed version
 * schedules a rebuild at startup. The rows live in `rows-derivers.ts`.
 */
export { DERIVED_STATE_STATUSES, DERIVER_NAMES, DERIVER_TRIGGERS, DERIVERS, DRIFT_KINDS, };
/** A deriver's name: its row in {@link DERIVERS} and its `derived_state.name`. */
export const DeriverName = z.enum(DERIVER_NAMES);
export const DeriverTrigger = z.enum(DERIVER_TRIGGERS);
/**
 * Which rows a write or a rebuild covers: every canon row (`all`; runtime derivers and
 * `db:rebuild` cover canon only, the seed writes reference rows), the documents a write touched,
 * the field definitions of a `field_def` transaction, the rows that follow the campaign clock, or
 * the rows a calendar commit recomputes.
 */
export const DeriveScope = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('all') }),
    z.strictObject({ kind: z.literal('documents'), documentIds: z.array(Uuid).min(1) }),
    z.strictObject({ kind: z.literal('field_defs'), fieldDefIds: z.array(Uuid).min(1) }),
    z.strictObject({ kind: z.literal('clock') }),
    z.strictObject({ kind: z.literal('calendar') }),
]);
/** How a stored derived row differs from its recomputation. */
export const DriftKind = z.enum(DRIFT_KINDS);
/** A `derived_state.status` value: `ok`, `stale` or `drift`. */
export const DerivedStateStatus = z.enum(DERIVED_STATE_STATUSES);
/**
 * One drifted row found by `verify`. `key` is the row's key as text: its uuid, or the JSON array
 * of its key values for a composite key. A key part that holds authored text (a name or an alias,
 * a title, a field value) is written as a hash of that text, 16 hexadecimal digits as 1.D's
 * `nameKey` gives it, never the text itself, so a drift never copies the GM's writing into the
 * Health page, a log or `db:verify`'s output (1.0b deliverable 16b). The schema does not check
 * this; each deriver that keys on text proves its keys in its own `verify` spec (1.D's `names`,
 * 1.B-1's `fields`). `detail` names the columns that differ and never copies their text either.
 */
export const DriftV1 = z.strictObject({
    deriver: DeriverName,
    table: z.string().min(1),
    key: z.string().min(1),
    kind: DriftKind,
    detail: z.string().max(500).nullable(),
});
/**
 * What a rebuild did. `unresolved` counts the items the deriver could not compute and recorded as
 * such (an invalid date, a vanished anchor) instead of failing.
 */
export const RebuildReportV1 = z.strictObject({
    deriver: DeriverName,
    version: z.number().int().min(1),
    scope: DeriveScope,
    rebuiltAt: IsoTimestamp,
    rowsWritten: z.number().int().min(0),
    rowsDeleted: z.number().int().min(0),
    unresolved: z.number().int().min(0),
    durationMs: z.number().int().min(0),
});
/** The registry row of a deriver; throws on an unknown name. */
export function deriverRow(name) {
    const row = DERIVERS.find((r) => r.name === name);
    if (!row)
        throw new Error(`unknown deriver "${String(name)}"`);
    return row;
}
//# sourceMappingURL=derivers.js.map